import { CSSObject } from '@emotion/core';
import dynamic from 'next/dynamic';
import { useCallback, useMemo } from 'react';

import { useDeleteBasket, useGetBasket } from '@api/basket';
import { BasketProduct } from '@api/basket/types';
import { ProductDetail } from '@api/catalog';
import { useMassCreateFavorites } from '@api/customers';

import { useAuth } from '@context/auth';
import { useCartContext } from '@context/cart';
import { useCompareAndFavoriteContext } from '@context/compare';

import LoadWrapper from '@components/controls/LoadWrapper';
import Popup from '@components/controls/Popup';

import { Button, colors, scale, typography } from '@scripts/gds';
import { getItemForMetrics, metricsPushEvent } from '@scripts/helpers';
import { useMedia } from '@scripts/hooks';

import BinSmallIcon from '@icons/16/bin.svg';
import HeartSmallIcon from '@icons/16/favorite.svg';

const ProductCardBasket = dynamic(() => import('@components/ProductCard/ProductCardBasket'), { ssr: false });
const CartFooter = dynamic(() => import('./CartFooter'), { ssr: false });

const btnStyles: CSSObject = {
    '> svg': {
        fill: colors.textSecondGrey,
        transition: 'fill ease 200ms',
    },
    ':hover': { '> svg': { fill: colors.primary } },
};

export const removeDuplicatesByCode = (products: BasketProduct[]): BasketProduct[] => {
    const seenCodes = new Set();

    return products.filter(item => {
        if (seenCodes.has(item.code)) {
            return false;
        }
        seenCodes.add(item.code);

        return true;
    });
};

const CartPopup = ({
    isOpen,
    closeHandler,
    openAuthPopup,
}: {
    isOpen: boolean;
    closeHandler: () => void;
    openAuthPopup: () => void;
}) => {
    const { xs, md } = useMedia();
    const { basketProducts, setBasketProducts, setBasketProductsServices } = useCartContext();
    const { isAuthUser } = useAuth();

    const { data: apiBasketData, isFetching } = useGetBasket(isAuthUser);
    const { mutateAsync: clearBasket, isLoading: isLoadingClear } = useDeleteBasket();

    const basketData = useMemo(() => apiBasketData?.data || [], [apiBasketData]);
    const productsList = useMemo(
        () => (isAuthUser ? basketData : basketProducts),
        [isAuthUser, basketData, basketProducts]
    );

    const clearBasketHandler = useCallback(() => {
        metricsPushEvent({
            event: 'remove_from_cart',
            ecommerce: {
                items: productsList.map(item => ({
                    ...getItemForMetrics(item as ProductDetail),
                    quantity: 0,
                })),
            },
        });

        if (isAuthUser) {
            clearBasket();
        } else {
            setBasketProducts([]);
        }
        setBasketProductsServices([]);
    }, [isAuthUser, productsList]);

    const { favoriteProductsData } = useCompareAndFavoriteContext();

    const { mutateAsync: massCreateFavorite } = useMassCreateFavorites();
    const addMassFavoritesHandler = useCallback(async () => {
        if (isAuthUser) {
            await massCreateFavorite({
                offer_ids: removeDuplicatesByCode(basketData).map(item => item.id),
            });
        } else {
            openAuthPopup();
        }
    }, [isAuthUser, basketData]);

    const basketInFavorites = useMemo(() => {
        return basketData.length > 0 && favoriteProductsData.length > 0
            ? removeDuplicatesByCode(basketData).every(basketItem =>
                  favoriteProductsData.map(favoriteItem => favoriteItem.offer_id).includes(basketItem.id)
              )
            : false;
    }, [basketData, favoriteProductsData]);

    return (
        <Popup
            isSwipeable
            isHighLevel
            isOpen={isOpen}
            onRequestClose={closeHandler}
            css={{ maxWidth: 'none', [xs]: { borderRadius: 0 } }}
            isCart
        >
            <LoadWrapper isLoading={isLoadingClear || isFetching}>
                <Popup.Body
                    css={{
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        height: '100%',
                        paddingLeft: 0,
                        paddingRight: 0,
                        paddingBottom: 0,
                    }}
                >
                    <div
                        css={{
                            display: 'flex',
                            gap: scale(4),
                            marginBottom: scale(5),
                            height: 'fit-content',
                            paddingLeft: scale(3),
                            paddingRight: scale(3),
                            [md]: {
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: scale(3, true),
                                marginBottom: scale(3),
                            },
                            [xs]: {
                                paddingLeft: scale(3, true),
                                paddingRight: scale(3, true),
                            },
                        }}
                    >
                        <h1 css={typography('h1Popup')}>Ваша корзина</h1>
                        <Button
                            role="button"
                            Icon={BinSmallIcon}
                            theme="transparent"
                            size="custom"
                            css={btnStyles}
                            onClick={clearBasketHandler}
                            data-testid="button-delete"
                        >
                            Удалить все товары
                        </Button>

                        <Button
                            role="button"
                            Icon={HeartSmallIcon}
                            theme="transparent"
                            size="custom"
                            css={btnStyles}
                            onClick={addMassFavoritesHandler}
                            disabled={basketInFavorites}
                            data-testid="button-basket"
                        >
                            {basketInFavorites ? 'Корзина добавлена в избранное' : 'Сохранить корзину в избранное'}
                        </Button>
                    </div>

                    {productsList.length > 0 ? (
                        <div
                            css={{
                                paddingLeft: scale(3),
                                paddingRight: scale(3),
                                height: '100%',
                                overflowY: 'auto',
                                [xs]: {
                                    paddingLeft: scale(3, true),
                                    paddingRight: scale(3, true),
                                },
                                '::-webkit-scrollbar': {
                                    width: 3,
                                    height: 3,
                                    backgroundColor: '#E0E5E7',

                                    '&-thumb': {
                                        backgroundColor: colors.textSecondGrey,
                                    },
                                },
                            }}
                        >
                            {productsList.map(item => (
                                <div key={item.id}>
                                    <ProductCardBasket productItem={item} />
                                    <hr css={{ margin: `${scale(2)}px 0` }} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <h3 css={{ paddingLeft: scale(3), paddingRight: scale(3) }}>Ваша корзина пуста</h3>
                    )}

                    {productsList.length > 0 && <CartFooter closeHandler={closeHandler} />}
                </Popup.Body>
            </LoadWrapper>
        </Popup>
    );
};

export default CartPopup;
